import React from 'react';
import { cn } from './cn';

// Base común a todos los controles de formulario.
const controlBase =
  'w-full rounded-md border border-hairline bg-white px-3 py-2 text-sm text-ink ' +
  'placeholder:text-sage/70 transition-colors ' +
  'focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/20 ' +
  'disabled:cursor-not-allowed disabled:bg-paper-2 disabled:text-sage';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ invalid, className, ...rest }, ref) => (
    <input
      ref={ref}
      className={cn(controlBase, invalid && 'border-danger focus:border-danger focus:ring-danger/20', className)}
      {...rest}
    />
  ),
);
Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ invalid, className, rows = 3, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(controlBase, 'resize-y', invalid && 'border-danger focus:border-danger focus:ring-danger/20', className)}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ invalid, className, children, style, ...rest }, ref) => (
    <select
      ref={ref}
      className={cn(controlBase, 'appearance-none bg-no-repeat pr-9', invalid && 'border-danger focus:border-danger focus:ring-danger/20', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%237a8472' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 0.6rem center',
        ...style,
      }}
      {...rest}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

export interface FieldProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  /** id del control al que apunta el label (conecta label↔control). */
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Envoltorio de campo: label + control + hint/error.
 * Uso: <Field label="..." htmlFor="x"><Input id="x" /></Field>
 */
export const Field: React.FC<FieldProps> = ({ label, hint, error, required, htmlFor, className, children }) => (
  <div className={cn('flex flex-col gap-1.5', className)}>
    {label && (
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <p className="text-xs text-danger">{error}</p>
    ) : (
      hint && <p className="text-xs text-sage">{hint}</p>
    )}
  </div>
);

export default Field;
